'use client';

import { c as _c } from "react/compiler-runtime";
import { useSelector } from '@legendapp/state/react';
import { type PropsWithChildren, createContext, useContext } from 'react';
import { usePersistedValue } from '@/hooks/usePersistedValue';
import type { CmsFleetCategory } from '@/services/cms-sdk';
import type { CodeRegionTuple, CompareFleetCategoryTuple, StoredFleetCategory, StoredFleetCategoryTuple } from './types';
interface VehicleCompareState {
  categories: CompareFleetCategoryTuple;
  categoryCount: number;
  clearAll: () => void;
  vlpUrl: string;
  toggleCategory: (category: StoredFleetCategory) => void;
}
const VehicleCompareContext = createContext<VehicleCompareState>({
  categories: [undefined, undefined, undefined],
  categoryCount: 0,
  clearAll: () => {},
  vlpUrl: '',
  toggleCategory: () => {}
});
export interface VehicleCompareContextProps {
  blockId: string | number;
  fleetCategories: CmsFleetCategory[];
  vlpPageUrl?: string;
}
export const getStorageKey = (blockId: string | number) => `vehicle-compare-${blockId}`;
export const getVlpUrlStorageKey = (blockId: string | number) => `${getStorageKey(blockId)}-vlp-url`;
export function VehicleCompareProvider(t0) {
  const $ = _c(29);
  const {
    blockId,
    children,
    fleetCategories,
    vlpPageUrl
  } = t0;
  let t1;
  if ($[0] !== blockId) {
    t1 = getStorageKey(blockId);
    $[0] = blockId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const storageKey = t1;
  const categories$ = usePersistedValue(storageKey);
  let t2;
  if ($[2] !== categories$) {
    t2 = () => categories$;
    $[2] = categories$;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const categories = useSelector(t2);
  let t3;
  if ($[4] !== blockId) {
    t3 = getVlpUrlStorageKey(blockId);
    $[4] = blockId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const vlpUrlStorageKey = t3;
  const vlpUrl$ = usePersistedValue(vlpUrlStorageKey);
  let t4;
  if ($[6] !== vlpUrl$) {
    t4 = () => vlpUrl$;
    $[6] = vlpUrl$;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const vlpUrl = useSelector(t4);
  let t5;
  if ($[8] !== categories$ || $[9] !== vlpPageUrl || $[10] !== vlpUrl$) {
    t5 = category => {
      if (vlpPageUrl !== undefined) {
        vlpUrl$.set(vlpPageUrl);
      }
      categories$.set(prev => {
        const newCategories = prev ? [prev[0] || undefined, prev[1] || undefined, prev[2] || undefined] : [undefined, undefined, undefined];
        const position = newCategories.findIndex(cat => cat ? cat.code === category.code && cat.region === category.region : false);
        if (position > -1) {
          newCategories[position] = undefined;
          return newCategories;
        }
        const freePosition = newCategories.findIndex(_temp);
        if (freePosition > -1) {
          newCategories[freePosition] = category;
        }
        return newCategories;
      });
    };
    $[8] = categories$;
    $[9] = vlpPageUrl;
    $[10] = vlpUrl$;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const toggleCategory = t5;
  let t6;
  if ($[12] !== categories$) {
    t6 = () => {
      categories$.set([undefined, undefined, undefined]);
    };
    $[12] = categories$;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const clearAll = t6;
  let codeRegionList;
  if ($[14] !== categories) {
    codeRegionList = [undefined, undefined, undefined];
    for (let i = 0; i < codeRegionList.length; i = i + 1, i) {
      const category_0 = categories?.[i];
      if (!category_0) {
        continue;
      }
      codeRegionList[i] = `${category_0.code}-${category_0.region}`;
    }
    $[14] = categories;
    $[15] = codeRegionList;
  } else {
    codeRegionList = $[15];
  }
  let categoriesToCompare;
  let t7;
  if ($[16] !== codeRegionList || $[17] !== fleetCategories) {
    categoriesToCompare = [undefined, undefined, undefined];
    for (let i_0 = 0; i_0 < codeRegionList.length; i_0 = i_0 + 1, i_0) {
      const codeRegion = codeRegionList[i_0];
      if (!codeRegion) {
        continue;
      }
      categoriesToCompare[i_0] = fleetCategories.find(category_1 => `${category_1.code}-${category_1.region}` === codeRegion);
    }
    t7 = categoriesToCompare.filter(_temp2);
    $[16] = codeRegionList;
    $[17] = fleetCategories;
    $[18] = categoriesToCompare;
    $[19] = t7;
  } else {
    categoriesToCompare = $[18];
    t7 = $[19];
  }
  const categoryCount = t7.length;
  const t8 = vlpUrl || "";
  let t9;
  if ($[20] !== categoriesToCompare || $[21] !== categoryCount || $[22] !== clearAll || $[23] !== t8 || $[24] !== toggleCategory) {
    t9 = {
      categories: categoriesToCompare,
      categoryCount,
      clearAll,
      vlpUrl: t8,
      toggleCategory
    };
    $[20] = categoriesToCompare;
    $[21] = categoryCount;
    $[22] = clearAll;
    $[23] = t8;
    $[24] = toggleCategory;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== children || $[27] !== t9) {
    t10 = <VehicleCompareContext.Provider value={t9}>{children}</VehicleCompareContext.Provider>;
    $[26] = children;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp2(cat_1) {
  return cat_1 !== undefined;
}
function _temp(cat_0) {
  return cat_0 === undefined;
}
export function useVehicleCompareState() {
  const context = useContext(VehicleCompareContext);
  if (!context) {
    throw new Error("useVehicleCompareState must be used within a VehicleCompareProvider");
  }
  return context;
}